<template>
  <div class="row m-t-40">
    <div :class="hasRightSideAction ? 'col-md-2' : 'col-md-12 text-right'">
      <button type="button" class="btn btn-default" data-dismiss="modal" @click="$emit('close')">
        {{ $t(closeButtonText) }}
      </button>
    </div>

    <div :class="`${hasRightSideAction ? 'col-md-10' : 'col-md-12'} text-right`">
      <WdsButton
        v-if="showUndoButton"
        secondary
        lg
        type="button"
        class="btn btn-secondary m-r-5"
        style="display: inline-block"
        @click="$emit('undo')"
      >
        {{ undoButtonText }}
      </WdsButton>

      <button
        v-if="showRequestButton"
        v-tooltip.top="
          $t(
            'You can prolong the contract or make other changes.<br> Your freelancer will then be able to accept the updated contract.',
          )
        "
        class="btn btn-primary"
        @click.prevent="$emit('edit')"
      >
        {{ $t(requestButtonText) }}
      </button>

      <button
        v-if="showCancelRequestButton"
        v-tooltip.top="
          $t(
            'This will cancel your pending change request, revert to the currently active contract, and notify the other party',
          )
        "
        :disabled="isSubmitting"
        :class="[
          'single-click',
          // Primary when it's the only action available (e.g. locked, scheduled changes).
          showSubmitButton ? 'btn btn-default' : 'btn btn-primary',
        ]"
        @click.prevent="$emit('cancelRequest')"
      >
        {{ $t('Cancel request') }}
      </button>

      <button
        v-if="showSubmitButton"
        ref="submitButton"
        v-tooltip.top="$t(submitButtonTooltip)"
        :disabled="submitButtonDisabled"
        type="submit"
        class="btn btn-primary single-click m-l-5"
        data-test="submit-contract-button"
        @click.prevent="onSubmitButtonPressed(false)"
      >
        <transition name="fade">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <button-spinner v-if="isSubmitting" size="sm" color="white" />
        </transition>
        {{ $t(submitButtonText) }}
      </button>
    </div>

    <WdsModal
      ref="confirmCurrencyModal"
      :show="showConfirmCurrencyModal"
      title="Confirm currency"
      @cancel="toggleConfirmCurrencyModal"
    >
      {{
        $t(
          "You've selected the worker's preferred currency for this job post. All bills and invoices under this job will be in that currency and not your own. Do you want to proceed?",
        )
      }}
      <template #footer>
        <WdsStack justify-space-between>
          <WdsButton @click="toggleConfirmCurrencyModal">{{ $t('Cancel') }}</WdsButton>
          <div class="wds-footer-action-buttons tw-ml-auto">
            <WdsButton primary @click="onConfirmSubmit">{{ $t('Confirm') }}</WdsButton>
          </div>
        </WdsStack>
      </template>
    </WdsModal>
  </div>
</template>

<script>
import { WdsStack, WdsInputArea, WdsModal, WdsButton, WdsSpinner } from '@worksome/wds-components'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

export default {
  name: 'ContractFormActionButtons',
  // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
  components: { FeatureFlagged, WdsModal, WdsInputArea, WdsStack, WdsButton, WdsSpinner },
  props: {
    /**
     * Set modal close button text
     */
    closeButtonText: {
      type: String,
      required: true,
    },
    /**
     * The number displayed in the header for denying the contract
     */
    contractNumber: {
      type: [String, Number],
      required: true,
    },
    /**
     * Set if request button should be shown.
     */
    showRequestButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Set request button text
     */
    requestButtonText: {
      type: String,
      required: true,
    },
    /**
     * Set if request changes cancek button should be shown.
     */
    showCancelRequestButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Set if undo button should be shown.
     */
    showUndoButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Set undo button text
     */
    undoButtonText: {
      type: String,
      required: true,
    },
    /**
     * Set if submit button should be shown.
     */
    showSubmitButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Set if submit button is disabled.
     */
    submitButtonDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Set submit button text
     */
    submitButtonText: {
      type: String,
      required: true,
    },
    /**
     * Set submit button tooltip
     */
    submitButtonTooltip: {
      type: String,
      default: '',
    },
    /**
     * Set if submit button is loading or in action.
     */
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    /**
     * If the form uses the FL currency (as opposed to the company currency)
     */
    usesFlCurrency: {
      type: Boolean,
      default: false,
    },
    validationErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['submitForm', 'close', 'denyContract', 'clearErrors'],
  data() {
    return {
      showConfirmCurrencyModal: false,
    }
  },
  computed: {
    hasRightSideAction() {
      return this.showSubmitButton || this.showRequestButton || this.showCancelRequestButton
    },
  },
  methods: {
    onSubmitButtonPressed(forced = false) {
      if (forced) {
        this.$emit('submitForm')
      } else if (this.usesFlCurrency) {
        this.toggleConfirmCurrencyModal()
      } else {
        this.$emit('submitForm')
      }
    },
    onConfirmSubmit() {
      this.toggleConfirmCurrencyModal()
      this.onSubmitButtonPressed(true)
    },
    toggleConfirmCurrencyModal() {
      this.showConfirmCurrencyModal = !this.showConfirmCurrencyModal
      if (this.showConfirmCurrencyModal) {
        this.$nextTick(() =>
          this.$refs.confirmCurrencyModal.$el.scrollIntoView({ behavior: 'smooth' }),
        )
      }
    },
  },
}
</script>
